import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import Translate from "../../utils/Translate";
import { useApi } from "../../utils/Api";
import AutocompleteSearchField from "../AutocompleteSearchField";
import ActorAutocompleteOption from "../ActorAutocompleteOption";
import { actorCompanyTypes, actorSearch } from "../../utils/actorSearch";
import { AlertDialog } from "../AlertDialog";
import { Computer, Error } from "@mui/icons-material";

const rowKey = (sp) => `${sp.partId}_${sp.currency}`;

const isSamePartAndCurrency = (sp1, sp2) => sp1.partId === sp2.partId && sp1.currency === sp2.currency;

const isSameDiscount = (sp1, sp2) =>
  isSamePartAndCurrency(sp1, sp2) && sp1.fixedPrice === sp2.fixedPrice && sp1.fixedPercent === sp2.fixedPercent;

function SpecialPriceCell({ specialPrice }) {
  // if (!specialPrice) {
  //   return <Box sx={{ color: "text.disabled" }}>{Translate.get("NoSpecialPrice")}</Box>;
  // }

  const priceText =
    specialPrice.fixedPrice !== null && specialPrice.fixedPrice !== undefined
      ? `${specialPrice.fixedPrice} ${specialPrice.currency ?? ""}`
      : specialPrice.fixedPercent !== null && specialPrice.fixedPercent !== undefined
        ? `${specialPrice.fixedPercent * 100} %`
        : "";

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {specialPrice.partNo || "-"}
      </Typography>
      <Box>{specialPrice.partDescription}</Box>
      <Box>{priceText}</Box>
    </Box>
  );
}

export default function CopySpecialPricesDialog({ targetActorId, targetActorName, onClose, onSaved }) {
  const [sourceCompany, setSourceCompany] = useState(null);
  const [targetCompany, setTargetCompany] = useState(
    targetActorId ? { actorId: targetActorId, actorName: targetActorName } : null,
  );
  const [sourceSpecialPrices, setSourceSpecialPrices] = useState([]);
  const [targetSpecialPrices, setTargetSpecialPrices] = useState([]);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [isLoadingTarget, setIsLoadingTarget] = useState(false);
  const [selectionByKey, setSelectionByKey] = useState({}); // partId_currency -> "source" | "target"
  const [longErrorMessage, setLongErrorMessage] = useState(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();

  const companySearch = useCallback(async (searchText) => {
    const foundActors = await actorSearch(
      searchText,
      actorCompanyTypes,
      ["ActorId", "ActorName", "OrgNo", "ActorEmail", "ExternalActorNo", "ExternalDataId"],
      50,
    );
    if (foundActors) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalDataId: actor.externalDataId,
        observationNote: actor.observationNote,
      }));
    }
  }, []);

  const renderCompanyOption = useCallback(
    (option) => (
      <Box display="flex" justifyContent="space-between" width="100%">
        <ActorAutocompleteOption actorName={option?.actorName} orgNo={option?.orgNo} email={option?.email} />
        {(option.externalDataId || option.observationNote) && (
          <Stack
            direction="column"
            justifyContent={
              option.externalDataId && option.observationNote
                ? "space-between"
                : option.externalDataId
                  ? "flex-start"
                  : "flex-end"
            }
          >
            {option.externalDataId && (
              <Box display="flex" width="80px" gap={1}>
                <Computer />
                <Box>{option.externalDataId ?? ""}</Box>
              </Box>
            )}
            {option.observationNote && (
              <Tooltip title={option.observationNote} arrow>
                <Error sx={{ color: "red" }} />
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    ),
    [],
  );

  const loadSpecialPrices = useCallback(
    async (actorId, actorName) => {
      const url = `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/special-prices`;
      const response = await api.fetch(url, false, "GET");
      if (response.isSuccessful) {
        const loadedSpecialPrices = response.value;
        const duplicates = loadedSpecialPrices.filter((sp, index) =>
          loadedSpecialPrices.some((other, otherIndex) => otherIndex !== index && isSamePartAndCurrency(sp, other)),
        );
        if (duplicates.length > 0) {
          const duplicatesText = duplicates
            .map((sp) => `${sp.partNo ?? ""} ${sp.partDescription ?? ""} ${sp.currency ?? ""}`)
            .join("\n");
          setLongErrorMessage(
            `${Translate.get("ErrorDuplicateSpecialPrices").replace("{companyName}", actorName)}\n\n${duplicatesText}`,
          );
          return [];
        }
        return loadedSpecialPrices;
      } else {
        return [];
      }
    },
    [api],
  );

  useEffect(() => {
    if (targetActorId) {
      setIsLoadingTarget(true);
      loadSpecialPrices(targetActorId, targetActorName).then((sps) => {
        setTargetSpecialPrices(sps);
        setIsLoadingTarget(false);
      });
    }
  }, [targetActorId, targetActorName, loadSpecialPrices]);

  const handleSourceChange = useCallback(
    async (company) => {
      setSelectionByKey({});
      setSourceCompany(company ?? null);
      if (company?.actorId > 0) {
        setIsLoadingSource(true);
        setSourceSpecialPrices(await loadSpecialPrices(company.actorId, company.actorName));
        setIsLoadingSource(false);
      } else {
        setSourceSpecialPrices([]);
      }
    },
    [loadSpecialPrices],
  );

  const handleTargetChange = useCallback(
    async (company) => {
      setSelectionByKey({});
      setTargetCompany(company ?? null);
      if (company?.actorId > 0) {
        setIsLoadingTarget(true);
        setTargetSpecialPrices(await loadSpecialPrices(company.actorId, company.actorName));
        setIsLoadingTarget(false);
      } else {
        setTargetSpecialPrices([]);
      }
    },
    [loadSpecialPrices],
  );

  const rows = useMemo(
    () =>
      sourceSpecialPrices
        // .filter((sourceSp) => !targetSpecialPrices.some((t) => isSameDiscount(t, sourceSp)))
        .map((sourceSp) => {
          const targetSpSame = targetSpecialPrices.find((t) => isSameDiscount(t, sourceSp)) ?? null;
          const targetSpConflict = !targetSpSame
            ? (targetSpecialPrices.find((t) => isSamePartAndCurrency(t, sourceSp)) ?? null)
            : null;
          return {
            key: rowKey(sourceSp),
            sourceSp,
            targetSp: targetSpSame ?? targetSpConflict,
            isSame: !!targetSpSame,
            isConflict: !!targetSpConflict,
          };
        })
        .sort((a, b) => {
          if (!!a.isConflict !== !!b.isConflict) {
            return a.isConflict ? -1 : 1;
          }
          if (!!a.isSame !== !!b.isSame) {
            return a.isSame ? -1 : 1;
          }
          return (a.sourceSp.partNo ?? "-").localeCompare(b.sourceSp.partNo ?? "-");
        }),
    [sourceSpecialPrices, targetSpecialPrices],
  );

  const conflictRows = useMemo(() => rows.filter((r) => r.isConflict), [rows]);

  const bothCompaniesSelected = sourceCompany?.actorId > 0 && targetCompany?.actorId > 0;
  const allConflictsResolved = conflictRows.every((r) => !!selectionByKey[r.key]);
  const isSaveDisabled =
    !bothCompaniesSelected ||
    sourceSpecialPrices.length === 0 ||
    isLoadingSource ||
    isLoadingTarget ||
    !allConflictsResolved;

  const saveSpecialPrice = useCallback(
    async (specialPrice) => {
      const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/special-prices`;
      return await api.fetchWithOverride(url, specialPrice, "POST", () => true); // suppress auto snackbar on error
    },
    [api],
  );

  async function handleSave() {
    const toSave = rows
      .filter((row) => !row.isSame) // No need to save since we just keep the existing one on target
      .filter((row) => !row.isConflict || selectionByKey[row.key] === "source") // No need to update when isConflict and "target" is selected
      .map((row) => {
        const chosen = row.isConflict && selectionByKey[row.key] === "target" ? row.targetSp : row.sourceSp;
        return {
          partId: chosen.partId,
          partNo: chosen.partNo, // Not really needed for backend, but for error messages below
          partDescription: chosen.partDescription, // Not really needed for backend, but for error messages below
          currency: chosen.currency,
          fixedPrice: chosen.fixedPrice ?? null,
          fixedPercent: chosen.fixedPercent ?? null,
          actorId: targetCompany.actorId,
        };
      });

    const results = [];
    for (const specialPrice of toSave) {
      const response = await saveSpecialPrice(specialPrice);
      results.push({ response, specialPrice });
    }

    const allOk = results.every((r) => r.response.isSuccessful);
    if (allOk) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      onSaved ? onSaved() : onClose();
    } else {
      setLongErrorMessage(createLongErrorMessage(results));
    }
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{Translate.get("CopySpecialPrices")}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="50%">
                    <AutocompleteSearchField
                      disableAddAsNew
                      requireSelection
                      label={Translate.get("CopyFromCompany")}
                      placeholder={Translate.get("CopyFromCompany")}
                      value={{
                        actorId: sourceCompany?.actorId ?? "",
                        actorName: sourceCompany?.actorName ?? "",
                      }}
                      onValueChange={handleSourceChange}
                      getOptionLabel={(option) => option?.actorName ?? ""}
                      renderOption={renderCompanyOption}
                      keyPropName="actorId"
                      search={companySearch}
                      hideOptionsFilter={
                        targetCompany?.actorId > 0 && ((option) => option.actorId === targetCompany.actorId)
                      }
                      minWidth="300px"
                    />
                  </TableCell>
                  <TableCell width="50%">
                    <AutocompleteSearchField
                      disableAddAsNew
                      requireSelection
                      disabled={!!targetActorId}
                      label={Translate.get("CopyToCompany")}
                      placeholder={Translate.get("CopyToCompany")}
                      value={{
                        actorId: targetCompany?.actorId ?? "",
                        actorName: targetCompany?.actorName ?? "",
                      }}
                      onValueChange={handleTargetChange}
                      getOptionLabel={(option) => option?.actorName ?? ""}
                      renderOption={renderCompanyOption}
                      keyPropName="actorId"
                      search={companySearch}
                      hideOptionsFilter={
                        sourceCompany?.actorId > 0 && ((option) => option.actorId === sourceCompany.actorId)
                      }
                      minWidth="300px"
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell align="left">
                      <Stack direction="row" alignItems="center">
                        <Radio
                          checked={selectionByKey[row.key] === "source" || (!row.isSame && !row.isConflict)}
                          disabled={row.isSame || !row.isConflict}
                          onChange={() => setSelectionByKey((prev) => ({ ...prev, [row.key]: "source" }))}
                          name={`radio-${row.key}`}
                        />

                        <SpecialPriceCell specialPrice={row.sourceSp} />
                      </Stack>
                    </TableCell>
                    <TableCell align="left">
                      {(row.isSame || row.isConflict) && (
                        <Stack direction="row" alignItems="center">
                          <Radio
                            checked={selectionByKey[row.key] === "target" || row.isSame}
                            disabled={row.isSame}
                            onChange={() => setSelectionByKey((prev) => ({ ...prev, [row.key]: "target" }))}
                            name={`radio-${row.key}`}
                          />
                          <SpecialPriceCell specialPrice={row.targetSp} />
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {bothCompaniesSelected && sourceSpecialPrices.length === 0 && !isLoadingSource && (
            <Box sx={{ marginTop: 2 }}>{Translate.get("NoSpecialPricesToCopy")}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
          <ButtonWithSpinner onClick={handleSave} disabled={isSaveDisabled} variant="contained">
            {Translate.get("Save")}
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
      <AlertDialog
        titleText={Translate.get("SomethingFailed")}
        bodyText={longErrorMessage}
        buttonText={Translate.get("Ok")}
        open={!!longErrorMessage}
        onClose={() => {
          setLongErrorMessage(null);
          onSaved ? onSaved() : onClose();
        }}
      />
    </>
  );
}

function createLongErrorMessage(results) {
  const failedResults = results.filter((r) => !r.response.isSuccessful);
  if (failedResults.length > 0) {
    const errorMessage = failedResults
      .map(({ response, specialPrice }) => {
        const message = response.errorMessageTranslationKey
          ? Translate.get(response.errorMessageTranslationKey)
          : Translate.get("SomethingFailed");
        const part = `${specialPrice.partNo ?? ""} ${specialPrice.partDescription ?? ""} (id: ${specialPrice.partId})`;
        return `${part}:\n${message}`;
      })
      .join("\n\n");
    return `${Translate.get("ErrorCopySpecialPrices")}\n\n${errorMessage}`;
  } else {
    return null;
  }
}
